import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import * as api from "../../api/client";
import type { ApiCtx } from "../../api/client";
import { Markdown } from "../../parse/markdown";
import { parsePlan, type PlanNode } from "../../parse/plan";
import { texte } from "../../texte";

function describeKind(node: PlanNode): string | undefined {
  if (node.kind === "for" && node.for) {
    const source = node.for.items_from ? `'${node.for.items_from}'` : JSON.stringify(node.for.items ?? []);
    const each = node.for.each?.id ?? node.for.each?.task ?? "";
    return `for über ${source}${each ? ` · each: ${each}` : ""}`;
  }
  if (node.kind === "while") {
    const cond = typeof node.while === "string" ? node.while : JSON.stringify(node.while);
    return `while ${cond} · max_retries ${node.max_retries ?? "?"}`;
  }
  if (node.kind === "if") {
    const cond = typeof node.if === "string" ? node.if : JSON.stringify(node.if);
    return `if ${cond}`;
  }
  return undefined;
}

function PlanBadge({ status }: { status?: string }) {
  const label = status ? texte.plan.statusLabel[status] ?? status : texte.plan.statusLabel.pending;
  const cls =
    status === "running"
      ? "st-running"
      : status === "done"
        ? "st-done"
        : status === "failed"
          ? "st-error"
          : status === "needs-fix"
            ? "st-waiting"
            : "";
  return (
    <span className={`badge ${cls}`}>
      <span className="dot" />
      {label}
    </span>
  );
}

function PlanNodeView({ node, open, toggle }: { node: PlanNode; open: Set<string>; toggle: (id: string) => void }) {
  const isOpen = open.has(node.id);
  const cond = describeKind(node);
  return (
    <div className={`pnode${isOpen ? " open" : ""}`}>
      <div
        className="row"
        role="button"
        tabIndex={0}
        onClick={() => toggle(node.id)}
        onKeyDown={(e) => e.key === "Enter" && toggle(node.id)}
      >
        <span className="pid">{node.id}</span>
        {node.kind && (
          <span className="kind">
            {node.kind}
            {node.kind === "for" && node.children?.length ? ` ×${node.children.length}` : ""}
          </span>
        )}
        {node.effort && <span className="effort">{node.effort}</span>}
        {cond && <span className="cond">{cond}</span>}
        <PlanBadge status={node.status} />
      </div>
      <div className="detail">
        <p>{node.task ?? ""}</p>
        {node.produces && node.produces.length > 0 && (
          <p className="hint" style={{ marginTop: 6 }}>
            {texte.plan.produces} <code>{Array.isArray(node.produces) ? node.produces.join(", ") : node.produces}</code>
          </p>
        )}
        {node.condition_result && (
          <p className="hint" style={{ marginTop: 6 }}>
            {texte.plan.conditionResult} <code>{node.condition_result}</code>
            {node.branch && (
              <>
                {" "}
                · {texte.plan.branch} <code>{node.branch}</code>
              </>
            )}
          </p>
        )}
        {node.result && <div className="res">{node.result}</div>}
      </div>
      {node.children && node.children.length > 0 && (
        <div className="children">
          {node.children.map((c) => (
            <PlanNodeView key={c.id} node={c} open={open} toggle={toggle} />
          ))}
        </div>
      )}
    </div>
  );
}

export function Plan({ ctx, planMode, showRawFallback }: { ctx: ApiCtx; planMode: boolean; showRawFallback: boolean }) {
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setOpen((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const query = useQuery({
    queryKey: ["doc", ctx.apiUrl, ctx.token, ctx.workspace, "plan"],
    queryFn: () => api.get(ctx, "plan"),
  });

  if (!query.data || query.data.code < 0 || !query.data.content) {
    return (
      <div className="card plan-empty" style={{ padding: 22 }}>
        <p>
          <b>{texte.plan.emptyTitle}</b>
        </p>
        <p className="hint" style={{ marginTop: 6 }}>
          {planMode ? texte.plan.emptyPlanMode : texte.plan.emptyClassicMode}
        </p>
      </div>
    );
  }

  // Classic mode (plan: false): agents may still write a freeform "plan"
  // workspace document (see workspace.CONTROL_DOCS / write_document). Show
  // it as markdown — there is no execution graph to parse.
  if (!planMode) {
    return <Markdown src={query.data.content} />;
  }

  let parsed;
  try {
    parsed = parsePlan(query.data.content);
  } catch {
    return (
      <div className="plan-empty">
        <p className="hint">{texte.plan.fallbackSimple}</p>
        {showRawFallback && <pre className="logbox">{query.data.content}</pre>}
      </div>
    );
  }

  return (
    <>
      <div className="plan-head">
        <span className="badge st-planned">
          <span className="dot" />
          {texte.plan.revision} {parsed.revision ?? 1}
        </span>
        <p className="plan-goal">{parsed.goal}</p>
      </div>
      <p className="hint" style={{ marginBottom: 12 }}>
        {texte.plan.hint}
      </p>
      <div className="plan-tree">
        {parsed.nodes.map((n) => (
          <PlanNodeView key={n.id} node={n} open={open} toggle={toggle} />
        ))}
      </div>
    </>
  );
}
